import type { AIConversationContext } from '@/types/aiChat'

/**
 * Whether the screen or working data changed since the session's last known context so the API
 * should be told on the next message (`UpdatedContext`).
 *
 * The API keeps the latest working snapshot and compacts older navigation notes; what it never
 * does is rebuild the cached system prompt (ORB-763). So this can afford to be honest rather than
 * sparing: a change missed here is a model reasoning about a screen the user has left.
 *
 * ⚑ `entityId` is compared, since ORB-768. Before it two records of one type on one surface looked
 * identical from here, so a thread that was opened on one contact and carried to another kept
 * answering about the first. The console's lists have no entity, so for them this compares
 * `undefined` with `undefined` — the point of the check is the surfaces that do.
 *
 * Pure, and exported for the console's API adapter, which asks the same question of a session it
 * resumed rather than one it opened.
 */
export function hasChatContextChanged(
  initial: AIConversationContext | null | undefined,
  current: AIConversationContext | null | undefined,
): boolean {
  if (!initial || !current) return false
  return (
    initial.location !== current.location ||
    initial.campaignId !== current.campaignId ||
    initial.webId !== current.webId ||
    initial.leadId !== current.leadId ||
    initial.assetId !== current.assetId ||
    initial.fileImportRowId !== current.fileImportRowId ||
    initial.entityId !== current.entityId ||
    initial.entityType !== current.entityType ||
    initial.viewMode !== current.viewMode ||
    // ⚑ Since ORB-796. Editing the chips is the console's version of navigating: the thread is
    // now about a different list, and it is what reopening the thread restores — so a filter
    // change that never reached the API would leave the thread pointing at the old list.
    initial.consoleFilter !== current.consoleFilter ||
    workingFields(initial) !== workingFields(current) ||
    JSON.stringify(initial.loadedData ?? null) !== JSON.stringify(current.loadedData ?? null) ||
    JSON.stringify(initial.availableActions) !== JSON.stringify(current.availableActions)
  )
}

/** Order-insensitive: two builders listing the same fields differently are the same screen. */
function workingFields(context: AIConversationContext): string {
  return JSON.stringify((context.availableFields ?? [])
    .map(field => [field.name, field.displayName, field.fieldType, field.currentValue,
      field.isEditable, field.isFocused, field.maxLength, field.allowedValues])
    .sort((left, right) => String(left[0]).localeCompare(String(right[0]))))
}
